// size-popup.js (improved)
// NOTE: Keep this file loaded with defer in theme.liquid: 
// <script src="{{ 'size-popup.js' | asset_url }}" defer></script>

(function () {
  'use strict';

  // ========================================================================
  // STATE & CACHE
  // ========================================================================
  const productCache = {};     // in-memory cache to reduce repeated /products/{handle}.js requests
  let pending = false;         // prevent double submits

  // ========================================================================
  // UTILITIES
  // ========================================================================
  function fetchJson(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(res => {
        if (!res.ok) throw new Error(`${url} returned ${res.status}`);
        return res.json();
      });
  }

  function showToast(message, time = 1400) {
    const t = document.createElement('div');
    t.textContent = message;

    Object.assign(t.style, {
      position: 'fixed',
      right: '12px',
      bottom: '12px',
      padding: '10px 14px',
      background: '#111',
      color: '#fff',
      borderRadius: '6px',
      zIndex: 12000
    });

    document.body.appendChild(t);
    setTimeout(() => t.remove(), time);
  }

  // Update cart count by fetching /cart.js and updating common selectors
  function updateCartUI() {
    return fetchJson('/cart.js')
      .then(cart => {
        // Common selectors - adapt if your theme uses different DOM
        const countEls = document.querySelectorAll(
          '.cart-count, .header-cart-count, .cart-bubble, [data-cart-count]'
        );

        countEls.forEach(el => {
          el.textContent = cart.item_count || 0;
        });

        // Try to open the cart drawer if theme exposes a function or a toggle button
        const drawerButton = document.querySelector(
          '[data-action="toggle-cart"], .cart-toggle, .header-cart-button, .site-header__cart'
        );

        if (drawerButton) {
          try { 
            drawerButton.click(); 
          } catch (e) { 
            /* ignore */ 
          }
        }

        return cart;
      })
      .catch(err => {
        console.warn('updateCartUI failed', err);
      });
  }

  // ========================================================================
  // AJAX CART HANDLER
  // ========================================================================
  function addToCartAjax(variantId, quantity = 1) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': 'application/json' 
      },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
    .then(res => {
      if (!res.ok) {
        return res.text().then(txt => {
          throw new Error(`Add to cart failed: ${res.status} ${txt}`);
        });
      }
      return res.json();
    })
    .then(data => {
      // ✅ Trigger cart refresh so bubble & drawer update immediately
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Still return the data for other uses (like your toast message)
      return data;
    });
  }

  // ========================================================================
  // PRODUCT VARIANTS (cached)
  // ========================================================================
  function getProductVariants(handle) {
    if (!handle) return Promise.reject(new Error('No product handle'));
    if (productCache[handle]) return Promise.resolve(productCache[handle]);

    return fetchJson(`/products/${encodeURIComponent(handle)}.js`)
      .then(product => {
        const variants = product.variants.map(v => ({
          id: v.id,
          title: v.title,
          available: v.available
        }));

        productCache[handle] = variants;
        return variants;
      });
  }

  // ========================================================================
  // UI BUILDERS
  // ========================================================================
  function buildInlinePanel(cardEl, variants, isBuyNow) {
    // Remove old ones
    closePanels();

    cardEl.style.position = cardEl.style.position || 'relative';

    const panel = document.createElement('div');
    panel.className = 'size-inline-panel';

    variants.forEach(v => {
      const item = document.createElement('div');
      item.className = 'size-item' + (v.available ? '' : ' disabled');
      item.textContent = v.title;

      if (v.available) item.dataset.variantId = v.id;

      item.addEventListener('click', function () {
        if (!v.available || pending) return;

        pending = true;
        disableButtons(true);

        addToCartAjax(v.id, 1)
          .then(() => {
            if (isBuyNow) {
              // ensure cart is updated before redirect
              return updateCartUI().finally(() => { 
                window.location.assign('/checkout'); 
              });
            } else {
              showToast(`Added ${v.title} to cart`);
              return updateCartUI();
            }
          })
          .catch(err => {
            console.error('Add to cart error', err);
            alert('Could not add to cart. Please try the product page.');
          })
          .finally(() => {
            pending = false;
            disableButtons(false);
            closePanels();
          });
      });

      panel.appendChild(item);
    });

    cardEl.appendChild(panel);

    // Close when clicking outside
    setTimeout(() => {
      document.addEventListener('click', outsideClick);
    }, 0);

    function outsideClick(e) {
      if (!cardEl.contains(e.target)) {
        closePanels();
        document.removeEventListener('click', outsideClick);
      }
    }
  }

  function buildModal(variants, isBuyNow) {
    closePanels();

    const overlay = document.createElement('div');
    overlay.className = 'size-popup-overlay';

    const modal = document.createElement('div');
    modal.className = 'size-popup-modal';

    modal.innerHTML = `
      <h3>
        Select Size 
        <span id="size-modal-close" style="float:right;cursor:pointer">✕</span>
      </h3>
    `;

    const list = document.createElement('ul');
    list.className = 'size-list';

    variants.forEach(v => {
      const li = document.createElement('li');
      li.textContent = v.title;

      if (v.available) {
        li.dataset.variantId = v.id;
      } else {
        li.className = 'disabled';
      }

      li.addEventListener('click', function () {
        if (!v.available || pending) return;

        pending = true;
        disableButtons(true);

        addToCartAjax(v.id, 1)
          .then(() => {
            if (isBuyNow) {
              return updateCartUI().finally(() => { 
                window.location.assign('/checkout'); 
              });
            }
            showToast(`Added ${v.title} to cart`);
            return updateCartUI();
          })
          .catch(err => {
            console.error(err);
            alert('Could not add to cart.');
          })
          .finally(() => {
            pending = false;
            disableButtons(false);
            closePanels();
          });
      });

      list.appendChild(li);
    });

    modal.appendChild(list);
    document.body.appendChild(overlay);
    document.body.appendChild(modal);

    overlay.addEventListener('click', closePanels);
    modal.querySelector('#size-modal-close').addEventListener('click', closePanels);
  }

  function closePanels() {
    document.querySelectorAll('.size-inline-panel, .size-popup-overlay, .size-popup-modal')
      .forEach(n => n.remove());
  }

  function disableButtons(bool) {
    document.querySelectorAll('.add-to-cart, .buy-now')
      .forEach(b => b.disabled = !!bool);
  }

  // ========================================================================
  // EVENT HANDLER
  // ========================================================================
  document.body.addEventListener('click', function (e) {
    const addBtn = e.target.closest('.add-to-cart');
    const buyBtn = e.target.closest('.buy-now');
    const trigger = addBtn || buyBtn;
    if (!trigger) return;

    // stop form submit or other default behavior
    e.preventDefault();

    if (pending) return; // prevent parallel actions

    const card = trigger.closest('.product-card');
    if (!card) return;

    const handle = card.dataset.productHandle || card.getAttribute('data-product-handle');
    if (!handle) return;

    const isBuyNow = !!buyBtn;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    // Fetch variants (cached) and show UI
    getProductVariants(handle)
      .then(variants => {
        if (!variants || variants.length === 0) throw new Error('No variants');
        if (isMobile) buildModal(variants, isBuyNow);
        else buildInlinePanel(card, variants, isBuyNow);
      })
      .catch(err => {
        console.error('Could not load variants for', handle, err);
        alert('Unable to load sizes quickly. Please open the product page to choose size.');
      });
  });

  // ========================================================================
  // DEBUG
  // ========================================================================
  window._sizePopup = { 
    clearCache: () => { 
      Object.keys(productCache).forEach(k => delete productCache[k]); 
      console.info('size popup cache cleared'); 
    } 
  };

})();
